import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaTrash,
  FaPlus,
  FaArrowLeft,
  FaUndo,
  FaCheckCircle,
  FaSignOutAlt,
  FaLock,
  FaCloudUploadAlt,
} from "react-icons/fa";
import { ICONS, ICON_LABELS, ICON_OPTIONS } from "./icons";
import "./Admin.css";

const API = "/.netlify/functions/admin";
const DRAFT_KEY = "ivanfranchi-admin-draft-v1";
const TOKEN_KEY = "ivanfranchi-admin-token-v1";

function newId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.links) || !Array.isArray(parsed.tourDates))
      return null;
    return parsed;
  } catch {
    return null;
  }
}

function getToken() {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function AdminLogin({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(API, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ action: "login", username, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.token) {
        try {
          sessionStorage.setItem(TOKEN_KEY, data.token);
        } catch {}
        onSuccess(data.token);
      } else {
        setError(data.error || "Sign in failed.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-login">
      <form className="login-card" onSubmit={submit}>
        <div className="login-icon">
          <FaLock />
        </div>
        <h1>Admin</h1>
        <p>Sign in to manage content.</p>
        <input
          type="text"
          value={username}
          autoFocus
          autoComplete="username"
          onChange={(e) => {
            setUsername(e.target.value);
            setError("");
          }}
          placeholder="Username"
          className={error ? "input-error" : ""}
        />
        <input
          type="password"
          value={password}
          autoComplete="current-password"
          onChange={(e) => {
            setPassword(e.target.value);
            setError("");
          }}
          placeholder="Password"
          className={error ? "input-error" : ""}
        />
        {error && <div className="login-error">{error}</div>}
        <button type="submit" className="primary-btn" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(getToken);

  const signOut = useCallback(() => {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch {}
    setToken(null);
  }, []);

  if (!token) return <AdminLogin onSuccess={setToken} />;
  return <AdminPanel onSignOut={signOut} />;
}

function AdminPanel({ onSignOut }) {
  const draft = useMemo(loadDraft, []);
  const [links, setLinks] = useState(draft?.links ?? []);
  const [tourDates, setTourDates] = useState(draft?.tourDates ?? []);
  const [hasDraft, setHasDraft] = useState(Boolean(draft));
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // "saving" | "saved" | error string
  const [serverContent, setServerContent] = useState(null);

  // Load current published content from the server.
  useEffect(() => {
    let active = true;
    fetch(API)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!active || !data) return;
        setServerContent(data);
        // Only adopt server content when there's no local draft in progress.
        if (!draft) {
          setLinks(data.links ?? []);
          setTourDates(data.tourDates ?? []);
        }
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [draft]);

  // Autosave a local draft so edits survive an accidental reload.
  useEffect(() => {
    if (loading) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ links, tourDates }));
      setHasDraft(true);
    } catch {}
  }, [links, tourDates, loading]);

  const resetToPublished = () => {
    if (!window.confirm("Discard your unsaved edits and reload the published content?"))
      return;
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {}
    setLinks(serverContent?.links ?? []);
    setTourDates(serverContent?.tourDates ?? []);
    setHasDraft(false);
  };

  const updateLink = (id, patch) =>
    setLinks((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const deleteLink = (id) => {
    if (!window.confirm("Delete this link?")) return;
    setLinks((prev) => prev.filter((l) => l.id !== id));
  };
  const moveLink = (id, dir) =>
    setLinks((prev) => {
      const idx = prev.findIndex((l) => l.id === id);
      const j = idx + dir;
      if (idx < 0 || j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  const addLink = () =>
    setLinks((prev) => [...prev, { id: newId(), label: "", url: "", icon: "link" }]);

  const updateTour = (id, patch) =>
    setTourDates((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  const deleteTour = (id) => {
    if (!window.confirm("Delete this show?")) return;
    setTourDates((prev) => prev.filter((t) => t.id !== id));
  };
  // Venue suggestions: the accumulated list from the server plus any places
  // currently typed into shows (so freshly added venues suggest right away).
  const venueOptions = useMemo(() => {
    const seen = new Map();
    const add = (v) => {
      const name = (v || "").trim();
      if (!name) return;
      const key = name.toLowerCase();
      if (!seen.has(key)) seen.set(key, name);
    };
    (serverContent?.venues ?? []).forEach(add);
    tourDates.forEach((t) => add(t.place));
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [serverContent, tourDates]);

  const addTour = () =>
    setTourDates((prev) => [
      ...prev,
      { id: newId(), date: "", time: "", place: "", mapsUrl: "" },
    ]);

  const publish = async () => {
    setStatus("saving");
    try {
      const res = await fetch(API, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          action: "save",
          token: getToken(),
          content: { links, tourDates },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setServerContent(data.content);
        try {
          localStorage.removeItem(DRAFT_KEY);
        } catch {}
        setHasDraft(false);
        setStatus("saved");
        setTimeout(() => setStatus(null), 3000);
      } else if (res.status === 401) {
        onSignOut();
      } else {
        setStatus(data.error || "Could not publish. Please try again.");
      }
    } catch {
      setStatus("Network error. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="admin">
        <p className="empty" style={{ marginTop: 48 }}>
          Loading…
        </p>
      </div>
    );
  }

  return (
    <div className="admin">
      <header className="admin-header">
        <button
          type="button"
          className="admin-back"
          aria-label="Back to site"
          onClick={() => {
            window.location.hash = "";
          }}
        >
          <FaArrowLeft />
        </button>
        <h1>Manage content</h1>
        <button
          type="button"
          className="admin-signout"
          aria-label="Sign out"
          onClick={onSignOut}
        >
          <FaSignOutAlt />
        </button>
      </header>

      {hasDraft && (
        <div className="admin-banner">
          <span>You have unpublished edits saved on this device.</span>
          <button className="ghost-btn" onClick={resetToPublished}>
            <FaUndo /> Reset
          </button>
        </div>
      )}

      <section className="admin-section">
        <div className="section-head">
          <h2>Links</h2>
        </div>
        {links.length === 0 && <p className="empty">No links yet.</p>}
        {links.map((link, i) => {
          const Icon = ICONS[link.icon] || ICONS.link;
          return (
            <div key={link.id} className="card">
              <div className="card-head">
                <Icon className="card-icon" />
                <span className="card-title">{link.label || "New link"}</span>
                <button
                  className="icon-btn danger"
                  onClick={() => deleteLink(link.id)}
                  aria-label="Delete link"
                >
                  <FaTrash />
                </button>
              </div>
              <label className="field">
                <span>Label</span>
                <input
                  type="text"
                  value={link.label}
                  placeholder="e.g. Spotify"
                  onChange={(e) => updateLink(link.id, { label: e.target.value })}
                />
              </label>
              <label className="field">
                <span>Link (URL)</span>
                <input
                  type="url"
                  inputMode="url"
                  value={link.url}
                  placeholder="https://…"
                  onChange={(e) => updateLink(link.id, { url: e.target.value })}
                />
              </label>
              <label className="field">
                <span>Icon</span>
                <select
                  value={link.icon}
                  onChange={(e) => updateLink(link.id, { icon: e.target.value })}
                >
                  {ICON_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {ICON_LABELS[o]}
                    </option>
                  ))}
                </select>
              </label>
              <div className="reorder">
                <button
                  className="ghost-btn"
                  disabled={i === 0}
                  onClick={() => moveLink(link.id, -1)}
                >
                  ↑ Move up
                </button>
                <button
                  className="ghost-btn"
                  disabled={i === links.length - 1}
                  onClick={() => moveLink(link.id, 1)}
                >
                  ↓ Move down
                </button>
              </div>
            </div>
          );
        })}
        <button className="add-btn" onClick={addLink}>
          <FaPlus /> Add link
        </button>
      </section>

      <section className="admin-section">
        <div className="section-head">
          <h2>Shows</h2>
        </div>
        {tourDates.length === 0 && <p className="empty">No shows yet.</p>}
        {tourDates.map((t) => (
          <div key={t.id} className="card">
            <div className="card-head">
              <span className="card-title">
                {t.place || (t.date ? t.date : "New show")}
              </span>
              <button
                className="icon-btn danger"
                onClick={() => deleteTour(t.id)}
                aria-label="Delete show"
              >
                <FaTrash />
              </button>
            </div>
            <div className="field-row">
              <label className="field">
                <span>Date</span>
                <input
                  className="datetime-input"
                  type="date"
                  value={t.date}
                  onChange={(e) => updateTour(t.id, { date: e.target.value })}
                />
              </label>
              <label className="field">
                <span>Time</span>
                <input
                  className="datetime-input"
                  type="time"
                  value={t.time || ""}
                  onChange={(e) => updateTour(t.id, { time: e.target.value })}
                />
              </label>
            </div>
            <label className="field">
              <span>Place</span>
              <input
                type="text"
                value={t.place}
                placeholder="City, Venue"
                list="venue-suggestions"
                onChange={(e) => updateTour(t.id, { place: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Google Maps link (optional)</span>
              <input
                type="url"
                inputMode="url"
                value={t.mapsUrl || ""}
                placeholder="Leave blank to auto-search the place"
                onChange={(e) => updateTour(t.id, { mapsUrl: e.target.value })}
              />
            </label>
          </div>
        ))}
        <button className="add-btn" onClick={addTour}>
          <FaPlus /> Add show
        </button>
        <datalist id="venue-suggestions">
          {venueOptions.map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>
      </section>

      <div className="admin-footer">
        {status === "saved" && (
          <div className="status-msg">
            <FaCheckCircle /> Published. Your changes are live.
          </div>
        )}
        {status && status !== "saving" && status !== "saved" && (
          <div className="status-msg error">{status}</div>
        )}
        <button className="primary-btn" onClick={publish} disabled={status === "saving"}>
          <FaCloudUploadAlt /> {status === "saving" ? "Publishing…" : "Publish changes"}
        </button>
        <p className="footer-note">
          Changes go live on the site immediately after publishing.
        </p>
      </div>
    </div>
  );
}
